import { Button, Content, ContentVariants, Skeleton, Split, SplitItem, Stack, StackItem } from "@patternfly/react-core";
import { useQuery } from "@tanstack/react-query";
import type { FC } from "react";
import { getCompanyAccountInfo } from "../../../api/hydra";
import CircuitIcon from "../../../assets/Icon-Red_Hat-Circuit-A-Red-RGB.svg";
import type { DragHandleProps } from "../../../constants/CardsStructure";
import { usePartnerInfo } from "../../../queries/partnerInfo";
import { DashboardCard } from "../shared/DashboardCard";

const TIER_COLORS: Record<string, string> = {
	premier: "#6753AC",
	advanced: "#009596",
	ready: "#3E8635",
	unaffiliated: "#6A6E73",
};

const MyCompanyCardBody: FC = () => {
	const { data: partnerInfo, isPending: isPartnerInfoLoading } = usePartnerInfo();

	const { data: accountInfoData, isLoading: isAccountInfoLoading } = useQuery({
		queryKey: ["companyAccountInfo"],
		queryFn: ({ signal }) => getCompanyAccountInfo(signal),
	});

	const companyName = partnerInfo?.accountDisplayName || partnerInfo?.accountName || "";
	const accountId = partnerInfo?.ebsAccountNumber || "";
	const currentTier = accountInfoData?.currentTier || "";
	const tierProgress = accountInfoData?.tierProgress;
	const currentPercent = tierProgress?.currentPercentage ?? 0;
	const nextTierPercent = tierProgress?.nextTierPercentage ?? 0;
	const enrolledModules = accountInfoData?.modules ?? [];
	const admins = accountInfoData?.orgAdmins?.slice(0, 2) ?? [];
	const accountManagers = accountInfoData?.partnerAccountManagers?.slice(0, 2) ?? [];

	return (
		<Split hasGutter style={{ minHeight: 200 }}>
			<SplitItem isFilled>
				<Stack hasGutter>
					<StackItem>
						{isPartnerInfoLoading ? (
							<>
								<Skeleton width="60%" fontSize="md" />
								<Skeleton width="40%" fontSize="sm" style={{ marginTop: 6 }} />
							</>
						) : (
							<Content>
								<Content component={ContentVariants.h4} style={{ marginBottom: 2 }}>
									{companyName}
								</Content>
								<Content component={ContentVariants.small}>Account ID: {accountId}</Content>
							</Content>
						)}
					</StackItem>

					<StackItem>
						{isAccountInfoLoading ? (
							<>
								<Skeleton width="40%" fontSize="sm" />
								<Skeleton width="70%" fontSize="sm" style={{ marginTop: 8 }} />
							</>
						) : currentTier ? (
							<>
								<span
									style={{
										backgroundColor: TIER_COLORS[currentTier.toLowerCase()] || "#009596",
										color: "#fff",
										fontWeight: 700,
										fontSize: "var(--pf-t--global--font--size--xs)",
										padding: "4px 12px",
										borderRadius: 4,
										display: "inline-block",
									}}
								>
									TIER: {currentTier.toUpperCase()}
								</span>
								{tierProgress && (
									<div style={{ marginTop: 8 }}>
										<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
											<div style={{ flex: 1, height: 12, position: "relative" }}>
												<div
													style={{
														position: "absolute",
														top: 0,
														left: 0,
														width: "100%",
														height: "100%",
														borderRadius: 6,
														backgroundColor: "#D2D2D2",
													}}
												/>
												<div
													style={{
														position: "absolute",
														top: 0,
														left: 0,
														width: `${Math.min(nextTierPercent, 100)}%`,
														height: "100%",
														borderRadius: 6,
														backgroundColor: "#73BCF7",
													}}
												/>
												<div
													style={{
														position: "absolute",
														top: 0,
														left: 0,
														width: `${currentPercent}%`,
														height: "100%",
														borderRadius: 6,
														backgroundColor: "#0066CC",
													}}
												/>
											</div>
											<Content
												component={ContentVariants.small}
												style={{ margin: 0, whiteSpace: "nowrap", fontWeight: 600 }}
											>
												{currentPercent}%
											</Content>
										</div>
										<Content component={ContentVariants.small} style={{ margin: 0, marginTop: 4 }}>
											Progress to next tier
										</Content>
									</div>
								)}
							</>
						) : null}
					</StackItem>

					<StackItem>
						{isAccountInfoLoading ? (
							<>
								<Skeleton width="50%" fontSize="md" />
								<Skeleton width="30%" fontSize="sm" style={{ marginTop: 4 }} />
								<Skeleton width="30%" fontSize="sm" style={{ marginTop: 4 }} />
							</>
						) : enrolledModules.length > 0 ? (
							<Content>
								<Content component={ContentVariants.h4} style={{ marginBottom: 4 }}>
									Module enrollment
								</Content>
								{enrolledModules.map((mod) => (
									<Content component={ContentVariants.p} key={mod} style={{ margin: 0 }}>
										{mod}
									</Content>
								))}
							</Content>
						) : null}
					</StackItem>
				</Stack>
			</SplitItem>

			<SplitItem isFilled>
				<Stack hasGutter>
					<StackItem>
						{isAccountInfoLoading ? (
							<>
								<Skeleton width="60%" fontSize="md" />
								<Skeleton width="50%" fontSize="sm" style={{ marginTop: 4 }} />
								<Skeleton width="45%" fontSize="sm" style={{ marginTop: 4 }} />
							</>
						) : admins.length > 0 ? (
							<>
								<Content>
									<Content component={ContentVariants.h4} style={{ marginBottom: 4 }}>
										Company administrators
									</Content>
								</Content>
								{admins.map((admin) => (
									<div key={admin.email}>
										<Button
											component="a"
											href={`mailto:${admin.email}`}
											variant="link"
											isInline
											style={{ fontSize: "var(--pf-t--global--font--size--sm)", paddingBlock: "2px" }}
										>
											{admin.name}
										</Button>
									</div>
								))}
							</>
						) : null}
					</StackItem>

					<StackItem>
						{isAccountInfoLoading ? (
							<>
								<Skeleton width="60%" fontSize="md" />
								<Skeleton width="50%" fontSize="sm" style={{ marginTop: 4 }} />
								<Skeleton width="45%" fontSize="sm" style={{ marginTop: 4 }} />
							</>
						) : accountManagers.length > 0 ? (
							<>
								<Content>
									<Content component={ContentVariants.h4} style={{ marginBottom: 4 }}>
										Red Hat account managers
									</Content>
								</Content>
								{accountManagers.map((manager) => (
									<div key={manager.email}>
										<Button
											component="a"
											href={`mailto:${manager.email}`}
											variant="link"
											isInline
											style={{ fontSize: "var(--pf-t--global--font--size--sm)", paddingBlock: "2px" }}
										>
											{manager.name}
										</Button>
									</div>
								))}
							</>
						) : null}
					</StackItem>
				</Stack>
			</SplitItem>
		</Split>
	);
};

export const MyCompanyCard: FC<{ dragHandleProps?: DragHandleProps | undefined }> = ({ dragHandleProps }) => (
	<DashboardCard
		icon={CircuitIcon}
		title="My company"
		isLoading={false}
		cardBody={<MyCompanyCardBody />}
		footerLinks={[
			{ label: "View program dashboard", href: "https://partners.connect.redhat.com/s/redhatpartnerprogrampage" },
			{ label: "View company information", href: "https://partners.connect.redhat.com/s/comm-my-account" },
		]}
		dragHandleProps={dragHandleProps}
	/>
);
